import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileCode, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { kindBadge } from "@/lib/kinds";

export const SkillCard = ({ skill, onClick, footer = null }) => {
  const navigate = useNavigate();
  const handle = onClick || (() => navigate(`/skill/${skill.id}`));
  const fileCount = skill.file_count ?? (skill.files?.length || 0);
  // 1 credit = 100 cents; strip a trailing .00 so whole credits read "2cr" not "2.00cr"
  const cents = skill.price_cents ?? 100;
  const price = cents === 0 ? "Free" : `${(cents / 100).toFixed(2).replace(/\.00$/, "")} cr`;
  return (
    <div
      data-testid="marketplace-skill-card"
      onClick={handle}
      className="group flex cursor-pointer flex-col rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5 transition-colors hover:border-[color:var(--rb-border-strong)]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{skill.category || "General"}</Badge>
          {kindBadge(skill.kind) && (
            <Badge data-testid="kind-badge" className="border-[color:rgba(94,234,212,0.3)] bg-[color:rgba(45,212,191,0.12)] text-[color:var(--rb-accent)]">{kindBadge(skill.kind)}</Badge>
          )}
        </div>
        <span className="rounded-full border border-[color:rgba(212,175,55,0.28)] bg-[color:var(--rb-gold-soft)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--rb-gold)]">{price}</span>
      </div>
      <h3 className="rb-mono mt-3 truncate text-[15px] font-semibold text-[color:var(--rb-text)]">{skill.slug}</h3>
      <p className="mt-1.5 line-clamp-2 text-sm text-[color:var(--rb-text-dim)]">{skill.short_description || skill.long_description || "A Claude Skill."}</p>

      <div className="mt-4 flex items-center justify-between">
        <StarRating rating={skill.avg_rating || 0} count={skill.review_count || 0} size={14} />
        <div className="flex items-center gap-3 text-xs text-[color:var(--rb-text-faint)]">
          <span className="flex items-center gap-1"><Download className="h-3.5 w-3.5" />{skill.download_count || 0}</span>
          <span className="flex items-center gap-1">{fileCount > 0 ? <FileCode className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}{fileCount + 1}</span>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 border-t border-[color:var(--rb-border)] pt-3">
        <Avatar className="h-5 w-5">
          <AvatarImage src={skill.creator_avatar} />
          <AvatarFallback className="bg-[color:var(--rb-surface)] text-[9px] text-[color:var(--rb-accent)]">{(skill.creator_name || "U").slice(0,1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="truncate text-xs text-[color:var(--rb-text-faint)]">{skill.creator_name ? `@${skill.creator_name}` : "Anonymous"}</span>
        {footer}
      </div>
    </div>
  );
};
